export const ERROR_CODES = {
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  VALIDATION: 'validation_failed',
  CONFLICT: 'conflict',
  RATE_LIMITED: 'rate_limited',
  /** A reset link that is unknown, expired, or already spent. */
  INVALID_TOKEN: 'invalid_token',
  OUT_OF_CREDITS: 'out_of_credits',
  UPSTREAM: 'upstream_failed',
  SAFETY: 'safety_blocked',
  INTERNAL: 'internal_error',
  NETWORK: 'network_error',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Copy we are willing to show a user mid-flow. Warm, never blaming. */
export const ERROR_COPY: Record<ErrorCode, string> = {
  unauthorized: 'You need to sign in to do that.',
  forbidden: "That one isn't yours to touch.",
  not_found: "We couldn't find that.",
  validation_failed: 'Something in there needs a second look.',
  conflict: 'That name is already taken.',
  rate_limited: 'Slow down a second — too many requests.',
  invalid_token: 'That link has expired or already been used. Ask for a fresh one.',
  out_of_credits: "You're out of fittings for now.",
  upstream_failed: 'The studio glitched. Try that again.',
  safety_blocked: "We couldn't generate that one. Try a different photo or garment.",
  internal_error: 'Something broke on our end.',
  network_error: 'No connection. Check your signal and try again.',
};

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields?: Record<string, string>;

  constructor(
    code: ErrorCode,
    message?: string,
    status = 400,
    fields?: Record<string, string>,
  ) {
    super(message ?? ERROR_COPY[code] ?? 'Something went wrong.');
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
  }

  static unauthorized(message?: string) {
    return new ApiError(ERROR_CODES.UNAUTHORIZED, message, 401);
  }
  static forbidden(message?: string) {
    return new ApiError(ERROR_CODES.FORBIDDEN, message, 403);
  }
  static notFound(message?: string) {
    return new ApiError(ERROR_CODES.NOT_FOUND, message, 404);
  }
  static validation(message?: string, fields?: Record<string, string>) {
    return new ApiError(ERROR_CODES.VALIDATION, message, 422, fields);
  }
  static conflict(message?: string) {
    return new ApiError(ERROR_CODES.CONFLICT, message, 409);
  }
  static rateLimited(message?: string) {
    return new ApiError(ERROR_CODES.RATE_LIMITED, message, 429);
  }
  /**
   * 400, deliberately not 401: a spent reset link is not a credentials problem
   * and must never send the client into its refresh-and-retry dance.
   */
  static invalidToken(message?: string) {
    return new ApiError(ERROR_CODES.INVALID_TOKEN, message, 400);
  }
  static outOfCredits(message?: string) {
    return new ApiError(ERROR_CODES.OUT_OF_CREDITS, message, 402);
  }
  static upstream(message?: string) {
    return new ApiError(ERROR_CODES.UPSTREAM, message, 502);
  }
  static safety(message?: string) {
    return new ApiError(ERROR_CODES.SAFETY, message, 422);
  }
  static internal(message?: string) {
    return new ApiError(ERROR_CODES.INTERNAL, message, 500);
  }

  toBody() {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.fields ? { fields: this.fields } : {}),
      },
    };
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/* ------------------------------------------------------------------ *
 * Safety copy
 *
 * Two rules for everything below. It must be warm — the person on the
 * other end uploaded a photo of themselves and is about to be told no.
 * And it must never lecture: we say what we can't do and what to try
 * next, and we do not explain their own photograph back to them.
 * ------------------------------------------------------------------ */

/**
 * Shown when a source photo is refused at upload. Keyed by the reason the
 * vision check returned; anything unrecognised falls back to `other`.
 */
export const BODY_SHOT_REJECTION_COPY: Record<string, string> = {
  nudity: "We can only work from a photo where you're dressed. Pick one in an outfit and we'll take it from there.",
  partial_nudity:
    "We can only work from a photo where you're dressed. Pick one in an outfit and we'll take it from there.",
  underwear_or_swimwear:
    "We need a photo in full clothes to dress you from — swimwear and underwear shots don't give us anything to build on. Try one in an outfit.",
  minor: 'We can only make looks for adults. Try a different photo.',
  no_person: 'We need a photo with you in it. Try one where you can see yourself head to toe.',
  unreadable: "We couldn't get a read on that photo. Try another one, or that same one again in a moment.",
  check_failed: "We couldn't get a read on that photo. Try another one, or that same one again in a moment.",
  upstream_blocked: "We couldn't use that photo. Try a different one.",
  other: "We couldn't use that photo. Try a different one.",
};

export function bodyShotRejectionCopy(reason: string): string {
  return BODY_SHOT_REJECTION_COPY[reason] ?? BODY_SHOT_REJECTION_COPY.other;
}

/**
 * Shown when a *generated* look fails the output gate.
 *
 * Deliberately does not say "try again". The identical input produces the
 * identical class of result, and a retry loop on a blocked combination just
 * burns the user's allowance. Name the real next move instead.
 */
export const LOOK_SAFETY_BLOCKED_COPY =
  "That combination didn't come out wearable. Running the same pieces again will land the same way — swap one out or change the mood and we'll go again. Your fitting is back in your balance.";
